import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ShieldCheck,
  Filter,
  Eye,
  ChevronLeft,
  ChevronRight,
  Send,
  CheckCircle2,
  AlertCircle,
  MessageSquare,
  KeyRound,
  Layers,
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/Card.js';
import { RoleBadge, AuthActionBadge, Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { SearchInput } from '../../components/ui/Input.js';
import { CopyButton } from '../../components/ui/CopyButton.js';
import { Modal } from '../../components/ui/Modal.js';
import { JsonViewer } from '../../components/ui/JsonViewer.js';
import { TableSkeleton } from '../../components/ui/Skeleton.js';
import { EmptyState, ErrorState } from '../../components/ui/EmptyState.js';
import { useI18n } from '../../context/I18nContext.js';
import { fetchAuditLogs } from '../../api/system.js';
import { formatDate, formatRelativeTime, truncateId } from '../../lib/utils.js';
import type { AuditLog } from '@hisob/types';

export function AuditLogsPage() {
  const [activeTab, setActiveTab] = useState<'all' | 'auth'>('auth');
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [inspectAudit, setInspectAudit] = useState<AuditLog | null>(null);
  const { t } = useI18n();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-audit-logs', { cursor, action: actionFilter, activeTab }],
    queryFn: () =>
      fetchAuditLogs({
        limit: 50,
        cursor,
        action: actionFilter !== 'all' ? actionFilter : undefined,
        category: activeTab,
      }),
    refetchInterval: 15000,
  });

  const allLogs = data?.items || [];

  const tabFilteredLogs = allLogs.filter((log) => {
    if (activeTab === 'auth') {
      const authActions = [
        'OTP_CODE_SENT',
        'OTP_BOT_NOT_STARTED',
        'LOGIN_SUCCESS',
        'LOGIN_FAILED',
        'OTP_VERIFIED',
        'OTP_VERIFICATION_FAILED',
        'TELEGRAM_LINKED',
      ];
      return authActions.includes(log.action) || log.target_type.includes('phone') || log.target_type.includes('auth');
    }
    return true;
  });

  const filteredLogs = tabFilteredLogs.filter((log) => {
    if (!search.trim()) return true;
    const term = search.toLowerCase();
    const phone = log.metadata?.phone_number ? String(log.metadata.phone_number).toLowerCase() : '';
    const chatId = log.metadata?.chat_id ? String(log.metadata.chat_id).toLowerCase() : '';

    return (
      log.id.toLowerCase().includes(term) ||
      log.action.toLowerCase().includes(term) ||
      log.actor_id.toLowerCase().includes(term) ||
      log.target_id.toLowerCase().includes(term) ||
      log.ip_hash.toLowerCase().includes(term) ||
      phone.includes(term) ||
      chatId.includes(term)
    );
  });

  // Auth Metrics Calculations: prefer real D1 server counts
  const otpSentCount = data?.auth_stats?.otp_sent ?? allLogs.filter((l) => l.action.startsWith('OTP_')).length;
  const loginSuccessCount = data?.auth_stats?.login_success ?? allLogs.filter((l) => l.action === 'LOGIN_SUCCESS' || l.action === 'OTP_VERIFIED').length;
  const loginFailedCount = data?.auth_stats?.login_failed ?? allLogs.filter((l) => l.action === 'LOGIN_FAILED' || l.action === 'OTP_VERIFICATION_FAILED').length;
  const telegramLinkedCount = data?.auth_stats?.telegram_linked ?? allLogs.filter((l) => l.action === 'TELEGRAM_LINKED').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            {t('audit.title')}
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            {t('audit.description')}
          </p>
        </div>

        {/* View Mode Tabs */}
        <div className="flex items-center rounded-lg border border-slate-200 dark:border-slate-800 p-1 bg-slate-50 dark:bg-slate-900">
          <button
            onClick={() => setActiveTab('auth')}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
              activeTab === 'auth'
                ? 'bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-xs'
                : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            <KeyRound className="w-3.5 h-3.5" />
            <span>{t('audit.tabAuth')}</span>
          </button>
          <button
            onClick={() => setActiveTab('all')}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
              activeTab === 'all'
                ? 'bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-xs'
                : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            <Layers className="w-3.5 h-3.5" />
            <span>{t('audit.tabAll')}</span>
          </button>
        </div>
      </div>

      {/* Auth Monitoring KPI Metrics (Shown when on Auth tab) */}
      {activeTab === 'auth' && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400">
                <Send className="w-5 h-5" />
              </div>
              <div>
                <span className="text-xs text-slate-500 dark:text-slate-400">OTP Sent</span>
                <div className="text-lg font-bold text-slate-900 dark:text-slate-100">{otpSentCount}</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <div>
                <span className="text-xs text-slate-500 dark:text-slate-400">Success</span>
                <div className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{loginSuccessCount}</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-rose-500/10 text-rose-600 dark:text-rose-400">
                <AlertCircle className="w-5 h-5" />
              </div>
              <div>
                <span className="text-xs text-slate-500 dark:text-slate-400">Failed</span>
                <div className="text-lg font-bold text-rose-600 dark:text-rose-400">{loginFailedCount}</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-sky-500/10 text-sky-600 dark:text-sky-400">
                <MessageSquare className="w-5 h-5" />
              </div>
              <div>
                <span className="text-xs text-slate-500 dark:text-slate-400">Telegram Linked</span>
                <div className="text-lg font-bold text-sky-600 dark:text-sky-400">{telegramLinkedCount}</div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filter Bar */}
      <Card>
        <CardContent className="p-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="w-full sm:w-80">
            <SearchInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('audit.searchPlaceholder')}
            />
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Filter className="w-4 h-4 text-slate-400" />
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              className="text-xs rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-slate-700 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">{t('audit.allActions')}</option>
              <option value="OTP_CODE_SENT">OTP_CODE_SENT</option>
              <option value="LOGIN_SUCCESS">LOGIN_SUCCESS</option>
              <option value="LOGIN_FAILED">LOGIN_FAILED</option>
              <option value="TELEGRAM_LINKED">TELEGRAM_LINKED</option>
              <option value="CONFIG_UPDATED">CONFIG_UPDATED</option>
              <option value="FEATURE_FLAG_UPDATED">FEATURE_FLAG_UPDATED</option>
              <option value="DEVICE_REVOKED">DEVICE_REVOKED</option>
              <option value="COMMAND_CREATED">COMMAND_CREATED</option>
              <option value="SNAPSHOT_ACCESSED">SNAPSHOT_ACCESSED</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Audit Log Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {isLoading ? (
            <div className="p-6">
              <TableSkeleton rows={8} cols={6} />
            </div>
          ) : error ? (
            <div className="p-6">
              <ErrorState title={t('audit.loadError')} message={(error as any).message} onRetry={() => refetch()} />
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="p-6">
              <EmptyState
                title={t('audit.emptyTitle')}
                description={t('audit.emptyDesc')}
                actionText={t('common.resetFilters')}
                onAction={() => {
                  setSearch('');
                  setActionFilter('all');
                }}
              />
            </div>
          ) : (
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 dark:bg-slate-900/60 border-b border-slate-200 dark:border-slate-800 text-slate-500 font-semibold uppercase tracking-wider">
                <tr>
                  <th className="py-3 px-4">{t('audit.tableTimestamp')}</th>
                  <th className="py-3 px-4">{t('audit.tableTarget')}</th>
                  <th className="py-3 px-4">{t('audit.tableAction')}</th>
                  <th className="py-3 px-4">{t('common.status')}</th>
                  <th className="py-3 px-4">Telegram Chat ID</th>
                  <th className="py-3 px-4">{t('audit.tableDetails')}</th>
                  <th className="py-3 px-4 text-right">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {filteredLogs.map((log) => {
                  const isAuthEvent =
                    log.action.includes('OTP') || log.action.includes('LOGIN') || log.action.includes('TELEGRAM');

                  const phone = log.metadata?.phone_number || (log.target_type.includes('phone') ? log.target_id : null);
                  const chatId = log.metadata?.chat_id || (log.target_type.includes('telegram') ? log.target_id : null);
                  const attempts: number | null = typeof log.metadata?.attempts === 'number' ? (log.metadata.attempts as number) : null;

                  return (
                    <tr key={log.id} className="hover:bg-slate-50 dark:hover:bg-slate-900/40 transition-colors">
                      {/* Vaqti */}
                      <td className="py-3 px-4 text-slate-500 whitespace-nowrap">
                        <span className="font-medium text-slate-800 dark:text-slate-200 block">
                          {formatRelativeTime(log.created_at)}
                        </span>
                        <span className="text-[10px] text-slate-400">
                          {formatDate(log.created_at)}
                        </span>
                      </td>

                      {/* Telefon Raqami yoki Target */}
                      <td className="py-3 px-4">
                        {phone ? (
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono font-bold text-slate-900 dark:text-slate-100">
                              {String(phone)}
                            </span>
                            <CopyButton value={String(phone)} truncate={false} className="p-0.5" />
                          </div>
                        ) : (
                          <div>
                            <span className="font-semibold text-slate-800 dark:text-slate-200">{log.target_type}:</span>{' '}
                            <span className="font-mono text-slate-500">{truncateId(log.target_id, 6)}</span>
                          </div>
                        )}
                      </td>

                      {/* Harakat (Action) */}
                      <td className="py-3 px-4">
                        {isAuthEvent ? (
                          <AuthActionBadge action={log.action} />
                        ) : (
                          <span className="font-mono font-bold text-indigo-600 dark:text-indigo-400">
                            {log.action}
                          </span>
                        )}
                      </td>

                      {/* Holati & Natija */}
                      <td className="py-3 px-4">
                        {log.action === 'OTP_CODE_SENT' ? (
                          <Badge variant="info">Sent (180s)</Badge>
                        ) : log.action === 'LOGIN_SUCCESS' || log.action === 'OTP_VERIFIED' ? (
                          <Badge variant="success">{t('common.success')}</Badge>
                        ) : log.action === 'LOGIN_FAILED' || log.action === 'OTP_VERIFICATION_FAILED' ? (
                          <Badge variant="danger">{t('common.failed')}</Badge>
                        ) : (
                          <RoleBadge role={log.actor_role as any} />
                        )}
                      </td>

                      {/* Telegram Chat ID */}
                      <td className="py-3 px-4">
                        {chatId ? (
                          <div className="flex items-center gap-1.5 font-mono text-[11px] text-sky-600 dark:text-sky-400">
                            <MessageSquare className="w-3.5 h-3.5 shrink-0 text-[#229ED9]" />
                            <span>{String(chatId)}</span>
                            <CopyButton value={String(chatId)} truncate={false} className="p-0.5" />
                          </div>
                        ) : (
                          <span className="text-slate-400 italic">—</span>
                        )}
                      </td>

                      {/* Urinishlar / Tafsilot */}
                      <td className="py-3 px-4">
                        {attempts !== null ? (
                          <span className={`font-mono text-xs font-semibold ${attempts > 1 ? 'text-amber-500' : 'text-slate-600'}`}>
                            {attempts} attempts
                          </span>
                        ) : (
                          <span className="font-mono text-[11px] text-slate-400">
                            IP: {truncateId(log.ip_hash, 4)}
                          </span>
                        )}
                      </td>

                      {/* Tafsilotlar Button */}
                      <td className="py-3 px-4 text-right">
                        <Button size="sm" variant="ghost" onClick={() => setInspectAudit(log)}>
                          <Eye className="w-3.5 h-3.5 mr-1" /> {t('common.view')}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>

        {/* Cursor Pagination */}
        {data && (
          <div className="flex items-center justify-between p-4 border-t border-slate-200 dark:border-slate-800 text-xs text-slate-500">
            <span>Showing: {filteredLogs.length} events</span>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={!cursor}
                onClick={() => setCursor(undefined)}
              >
                <ChevronLeft className="w-3.5 h-3.5 mr-1" /> {t('common.firstPage')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={!data.has_more || !data.next_cursor}
                onClick={() => setCursor(data.next_cursor || undefined)}
              >
                {t('common.nextPage')} <ChevronRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Inspect Audit Log Modal */}
      {inspectAudit && (
        <Modal
          isOpen={Boolean(inspectAudit)}
          onClose={() => setInspectAudit(null)}
          title={t('audit.inspectModalTitle')}
          maxWidth="lg"
        >
          <div className="max-h-[75vh] overflow-y-auto pr-1 space-y-4 text-xs">
            {/* Core Event Overview */}
            <div className="grid grid-cols-2 gap-3 p-3.5 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
              <div>
                <span className="text-slate-400 block text-[11px]">Audit ID</span>
                <span className="font-mono font-medium">{inspectAudit.id}</span>
              </div>
              <div>
                <span className="text-slate-400 block text-[11px]">Ijrochi & Rol</span>
                <span className="font-mono font-medium">{inspectAudit.actor_id} ({inspectAudit.actor_role})</span>
              </div>
              <div>
                <span className="text-slate-400 block text-[11px]">Harakat (Action)</span>
                <span className="font-mono font-bold text-indigo-600 dark:text-indigo-400">{inspectAudit.action}</span>
              </div>
              <div>
                <span className="text-slate-400 block text-[11px]">Vaqt (UTC & Mahalliy)</span>
                <span className="font-mono">{formatDate(inspectAudit.created_at)}</span>
              </div>
              <div className="col-span-2">
                <span className="text-slate-400 block text-[11px]">IP Hash</span>
                <span className="font-mono break-all text-slate-500">{inspectAudit.ip_hash}</span>
              </div>
            </div>

            {/* Quick Metadata Highlights for Auth Events */}
            {Boolean(inspectAudit.metadata && (inspectAudit.metadata.phone_number || inspectAudit.metadata.chat_id || inspectAudit.metadata.reason)) && (
              <div className="p-3 rounded-xl bg-indigo-50/50 dark:bg-indigo-950/20 border border-indigo-100 dark:border-indigo-900/40 grid grid-cols-2 gap-2 text-xs">
                {Boolean(inspectAudit.metadata?.phone_number) && (
                  <div>
                    <span className="text-slate-400 text-[11px] block">Telefon raqam:</span>
                    <span className="font-mono font-semibold text-slate-800 dark:text-slate-200">
                      {String(inspectAudit.metadata?.phone_number)}
                    </span>
                  </div>
                )}
                {Boolean(inspectAudit.metadata?.chat_id) && (
                  <div>
                    <span className="text-slate-400 text-[11px] block">Telegram Chat ID:</span>
                    <span className="font-mono font-semibold text-indigo-600 dark:text-indigo-400">
                      {String(inspectAudit.metadata?.chat_id)}
                    </span>
                  </div>
                )}
                {inspectAudit.metadata?.attempts !== undefined && (
                  <div>
                    <span className="text-slate-400 text-[11px] block">Urinishlar:</span>
                    <span className="font-mono font-semibold text-amber-600">
                      {String(inspectAudit.metadata?.attempts)} / 5
                    </span>
                  </div>
                )}
                {Boolean(inspectAudit.metadata?.reason) && (
                  <div>
                    <span className="text-slate-400 text-[11px] block">Xatolik sababi:</span>
                    <span className="font-mono text-rose-600 font-medium">
                      {String(inspectAudit.metadata?.reason)}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Structured JSON Metama'lumotlar Viewer */}
            <div>
              <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1.5">
                Batafsil xom metama'lumotlar (JSON)
              </label>
              <JsonViewer data={inspectAudit.metadata || { note: 'No metadata available' }} maxHeight="320px" />
            </div>

            <div className="flex justify-end pt-3 border-t border-slate-100 dark:border-slate-800">
              <Button variant="outline" size="sm" onClick={() => setInspectAudit(null)}>
                {t('common.close')}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
